import {
  takeLatest, put, call, all, select,
} from 'redux-saga/effects';
import UserGroupsService from '@services/user-groups';
import history from '@rootCommon/utilities/history';
import * as actions from '@actions';
import { RootState } from '@reducers';
import { BaseMessageDto, IdType } from '@rootCommon/types/common';
import { Filters } from '@rootCommon/types/filter';
import Message from '@rootCommon/services/message';
import { UserGroup, UserGroupDto, UserGroupListItem } from '@rootCommon/types/user-group';
import { ROUTES } from '@common/constants/routes';

import { AxiosError } from 'axios';
import { SagaGenerator } from './index';

// fetch all
function* fetchUserGroupsAsync() {
  const filters: Filters = yield select((state: RootState) => state.userGroups.filter);

  try {
    const userGroups: UserGroupListItem[] = yield call(UserGroupsService.getList, filters);

    yield put(actions.fetchUserGroups.success({ items: userGroups, total: userGroups?.length }));
  } catch (e) {
    const error = e as AxiosError | Error;
    yield put(actions.fetchUserGroups.failure(error));
  }
}

function* watchFetchUserGroups() {
  yield takeLatest(actions.fetchUserGroups.request, fetchUserGroupsAsync);
}

// fetch
function* fetchUserGroupAsync(action: { payload: IdType }) {
  try {
    const userGroups: UserGroup = yield call(UserGroupsService.getById, action.payload);

    yield put(actions.fetchUserGroup.success(userGroups));
  } catch (e) {
    const error = e as AxiosError | Error;
    yield put(actions.fetchUserGroup.failure(error));
  }
}

function* watchFetchUserGroup() {
  yield takeLatest(actions.fetchUserGroup.request, fetchUserGroupAsync);
}

// create
function* createUserGroupAsync(action: { payload: UserGroupDto }) {
  try {
    const userGroups: UserGroup = yield call(UserGroupsService.create, action.payload);

    yield put(actions.createUserGroup.success(userGroups));
    Message.success(`${action.payload.name || 'User'} has been created`);

    yield put(actions.fetchUserGroups.request());
    history.push(ROUTES.GROUPS.LIST);
  } catch (e) {
    const error = e as AxiosError | Error;
    yield put(actions.createUserGroup.failure(error));
  }
}

function* watchCreateUserGroup() {
  yield takeLatest(actions.createUserGroup.request, createUserGroupAsync);
}

// create modal
function* createUserGroupModalAsync(action: { payload: UserGroupDto }) {
  try {
    const userGroup: UserGroup = yield call(UserGroupsService.create, action.payload);

    yield put(actions.createUserGroupModal.success(userGroup));

    // yield put(actions.fetchUserGroups.request());
    yield put(actions.setCreatedUserGroupId(userGroup.id));
    yield put(actions.setUserGroupId(userGroup.id));
  } catch (e) {
    const error = e as AxiosError | Error;
    yield put(actions.createUserGroupModal.failure(error));
  }
}

function* watchCreateUserModalGroup() {
  yield takeLatest(actions.createUserGroupModal.request, createUserGroupModalAsync);
}

// update
function* updateUserGroupAsync(action: { payload: { id: IdType; data: UserGroupDto } }) {
  try {
    const userGroups: UserGroup = yield call(UserGroupsService.update, action.payload.id, action.payload.data);

    yield put(actions.updateUserGroup.success(userGroups));
    Message.success(`${action.payload.data.name || 'User'} has been updated`);
    yield put(actions.fetchUserGroups.request());
    history.push(ROUTES.GROUPS.LIST);
  } catch (e) {
    const error = e as AxiosError | Error;
    yield put(actions.updateUserGroup.failure(error));
  }
}

function* watchUpdateUserGroup() {
  yield takeLatest(actions.updateUserGroup.request, updateUserGroupAsync);
}

// delete
function* deleteUserGroupAsync(action: { payload: IdType }) {
  try {
    const answer: BaseMessageDto = yield call(UserGroupsService.delete, action.payload);

    yield put(actions.deleteUserGroup.success(action.payload));
    Message.success(answer.message);
    history.push(ROUTES.GROUPS.LIST);
  } catch (e) {
    const error = e as AxiosError | Error;
    yield put(actions.deleteUserGroup.failure(error));
  }
}

function* watchDeleteUserGroup() {
  yield takeLatest(actions.deleteUserGroup.request, deleteUserGroupAsync);
}

// filters
function* setUserGroupsFilterAsync() {
  yield put(actions.fetchUserGroups.request());
}

function* watchUserGroupsFilter() {
  yield takeLatest(actions.setUserGroupsFilter, setUserGroupsFilterAsync);
}

// all
function* watchUserGroups(): SagaGenerator {
  yield all([
    watchFetchUserGroups(),
    watchFetchUserGroup(),
    watchCreateUserGroup(),
    watchCreateUserModalGroup(),
    watchUpdateUserGroup(),
    watchDeleteUserGroup(),
    watchUserGroupsFilter(),
  ]);
}

export default watchUserGroups;
